/**
 * MODIFIED FROM SHADCN ORIGINAL
 *
 * This component has been customized from the original shadcn/ui version.
 * Modifications:
 * - Added cursor-pointer to Trigger
 * - Added iconPlacement prop to control chevron position (before/after text)
 *
 * Original: https://ui.shadcn.com/
 */

import { cn } from '@ophelos/ui/lib/utils';
import * as AccordionPrimitive from '@radix-ui/react-accordion';
import { ChevronUpIcon } from 'lucide-react';
import type * as React from 'react';

function Accordion({ ...props }: React.ComponentProps<typeof AccordionPrimitive.Root>) {
  return <AccordionPrimitive.Root data-slot="accordion" {...props} />;
}

function AccordionItem({ className, ...props }: React.ComponentProps<typeof AccordionPrimitive.Item>) {
  return <AccordionPrimitive.Item data-slot="accordion-item" className={cn('border-b last:border-b-0', className)} {...props} />;
}

type AccordionTriggerProps = React.ComponentProps<typeof AccordionPrimitive.Trigger> & {
  iconPlacement?: 'before' | 'after';
};

function AccordionTrigger({ className, children, iconPlacement = 'after', ...props }: AccordionTriggerProps) {
  const icon = (
    <ChevronUpIcon className="text-muted-foreground pointer-events-none size-4 shrink-0 translate-y-0.5 transition-transform duration-200" />
  );

  return (
    <AccordionPrimitive.Header className="flex">
      <AccordionPrimitive.Trigger
        data-slot="accordion-trigger"
        className={cn(
          'cursor-pointer focus-visible:highlight-border-surround hover:bg-card flex flex-1 items-baseline gap-4 rounded-md py-4 text-left text-sm font-medium transition-all disabled:pointer-events-none disabled:opacity-50 [&[data-state=open]>svg]:rotate-180',
          iconPlacement === 'before' ? '' : 'justify-between',
          className
        )}
        {...props}
      >
        {iconPlacement === 'before' && icon}
        {children}
        {iconPlacement === 'after' && icon}
      </AccordionPrimitive.Trigger>
    </AccordionPrimitive.Header>
  );
}

function AccordionContent({ className, children, ...props }: React.ComponentProps<typeof AccordionPrimitive.Content>) {
  return (
    <AccordionPrimitive.Content
      data-slot="accordion-content"
      className="data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down overflow-hidden text-sm"
      {...props}
    >
      <div className={cn('pt-0 pb-4', className)}>{children}</div>
    </AccordionPrimitive.Content>
  );
}

export { Accordion, AccordionContent, AccordionItem, AccordionTrigger };
